import IconArrowBack2 from "@/icons/IconArrowBack2";
import * as React from "react";
import { Link } from "react-router-dom";
import { StickyHeaderContext } from "@/context/StickyHeaderContext";
import { useNavigationTrail } from "@/hooks/useNavigationTrail";

interface Props {
  title: string;
  subTitle?: string;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  /** Optional explicit back destination. When omitted, falls back automatically
   *  to the real navigation trail's previous entry (wherever the user actually
   *  came from) via goBack() — no per-page wiring required. */
  backLink?: string;
  tabs?: React.ReactNode;
}

export default function StickyRouteHeader(props: Props) {
  const { setHasStickyHeader, toggleSidebar } = React.useContext(StickyHeaderContext);
  const { trail, goBack } = useNavigationTrail();

  // Signal MainLayout synchronously (before paint) so it suppresses its standalone breadcrumb.
  React.useLayoutEffect(() => {
    setHasStickyHeader(true);
    return () => setHasStickyHeader(false);
  }, [setHasStickyHeader]);

  const px = toggleSidebar ? "px-6" : "px-16";
  const contentPad = toggleSidebar ? "p-3 sm:p-6 pt-4" : "py-3 sm:py-6 pr-3 sm:pr-6 pl-16 pt-4";

  // MainLayout already renders the trail-based breadcrumb bar above this header (it's no longer
  // gated on hasStickyHeader), so this header only needs the back arrow — not a second crumb list.
  const hasAutoBack = trail.length > 1;

  return (
    <>
      <header
        className="shrink-0 sticky top-0 z-50 bg-white border-b border-zinc-200"
        style={{ isolation: "isolate" }}
      >
        <div className={`flex items-center justify-between py-3 ${px}`}>
          <div className="text-lg font-medium text-zinc-800 flex flex-row items-center">
            {(props.backLink || hasAutoBack) && (
              <span className="mr-2">
                {props.backLink ? (
                  <Link to={props.backLink} aria-label="Back">
                    <IconArrowBack2 />
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={goBack}
                    aria-label="Back"
                    className="appearance-none bg-transparent border-0 p-0 cursor-pointer"
                  >
                    <IconArrowBack2 />
                  </button>
                )}
              </span>
            )}
            <div className="flex flex-col">
              <h1 className="m-0 text-base font-semibold text-zinc-800 leading-snug">{props.title}</h1>
              {props.subTitle && (
                <span className="text-sm font-normal text-zinc-500 dark:text-zinc-300 mt-0.5">
                  {props.subTitle}
                </span>
              )}
            </div>
          </div>
          {props.headerActions && <span>{props.headerActions}</span>}
        </div>

        {props.tabs && (
          <nav aria-label="page tabs" className={`flex overflow-x-auto ${px}`}>
            {props.tabs}
          </nav>
        )}
      </header>

      {/* Content wrapper restores the padding that <main> no longer provides */}
      <div className={contentPad}>
        {props.children}
      </div>
    </>
  );
}
